import { ID } from '../types';
import BaseObject from './BaseObject';
import { OrderedMap } from 'immutable';

const defaultErrorText =
  'Nexus was not able to answer this query correctly. Our team has been notified and will issue a fix — please try again soon or';

export class ConversationMessage extends BaseObject {
  text: string;
  fromUser: boolean;
  relevantContacts: ID[];
  relevantGroups: ID[];
  terminated: boolean;

  constructor(props: any) {
    super(props);
    this.text = props.text;
    this.fromUser = props.fromUser;
    this.relevantContacts = props.relevantContacts || [];
    this.relevantGroups = props.relevantGroups || [];
    this.terminated = props.terminated || false;
  }

  buildMessage(chunk: string): ConversationMessage {
    return this.copyWith({ text: this.text + chunk });
  }
}

export class Conversation extends BaseObject {
  initialID: ID;
  messages: OrderedMap<ID, ConversationMessage>;
  targetContactID?: ID;
  paywalled?: boolean;
  nexusDisabled?: boolean;

  constructor(props: any) {
    super(props);
    this.initialID = props.initialID || props.id;
    this.targetContactID = props.targetContactID;
    this.paywalled = !!props.paywalled;
    this.nexusDisabled = !!props.nexusDisabled;

    if (props.messages) {
      this.messages = props.messages;
    } else {
      const firstMessage = new ConversationMessage({ text: props.prompt, fromUser: true });
      this.messages = OrderedMap([[firstMessage.id, firstMessage]]);
    }
  }

  addUserPrompt(prompt: string): Conversation {
    const newMessage = new ConversationMessage({ text: prompt, fromUser: true });
    return this.copyWith({
      messages: this.messages.set(newMessage.id, newMessage),
      isFetching: true,
    });
  }

  // Not sure why, but if we include the "not set" value in a single .update call, we sometimes
  // end up with the first chunk of the message getting repeated twice. Using the manual check
  // is cumbersome, but it avoids this issue.
  buildBotResponse(response: any): Conversation {
    if (this.messages.has(response.message_id)) {
      return this.copyWith({
        messages: this.messages.update(response.message_id, (message) =>
          message.buildMessage(response.segment),
        ),
        isFetching: false,
      });
    } else {
      return this.copyWith({
        messages: this.messages.set(
          response.message_id,
          new ConversationMessage({
            id: response.message_id,
            text: response.segment,
            fromUser: false,
            reasoning: response.reasoning,
            relevantContacts: response.relevant_contacts,
            relevantGroups: response.relevant_groups,
          }),
        ),
        isFetching: false,
      });
    }
  }

  regenerateLastBotResponse(): Conversation {
    return this.copyWith({
      messages: this.messages.delete(this.messages.last().id),
      isFetching: true,
    });
  }

  setRelevantContactsForBotResponse(messageID: ID, relevantContacts: ID[]): Conversation {
    return this.copyWith({
      messages: this.messages.update(
        messageID,
        new ConversationMessage({ id: messageID, text: '', fromUser: false, relevantContacts }),
        (message) => message.copyWith({ relevantContacts }),
      ),
    });
  }

  terminateBotResponse(messageID: ID): Conversation {
    return this.copyWith({
      messages: this.messages.update(
        messageID,
        new ConversationMessage({ id: messageID, text: '', fromUser: false, terminated: true }),
        (message) => message.copyWith({ terminated: true }),
      ),
    });
  }

  setBotResponseError(messageID?: ID): Conversation {
    if (this.messages.last().error) {
      return this;
    }

    return this.copyWith({
      messages: this.messages.update(
        messageID,
        new ConversationMessage({
          fromUser: false,
          text: defaultErrorText,
          error: true,
        }),
        (message) =>
          message.copyWith({
            text: defaultErrorText,
            error: true,
          }),
      ),
      isFetching: false,
    });
  }

  setTargetContactID(contactID: ID): Conversation {
    return this.copyWith({ targetContactID: contactID });
  }

  includeTargetContactID(contactIDs: ID[]): ID[] {
    if (!this.targetContactID) {
      return contactIDs;
    }

    return [this.targetContactID, ...contactIDs.filter((id) => id !== this.targetContactID)];
  }
}
